/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { configuration } from "@/lib/configuration";
import { safeLocalStorage } from "@/helper/safeLocalStorage";
import * as Sentry from "@sentry/react";
import {
  MemoryCache,
  LocalStorageCache,
  RequestDeduplicator,
  DEFAULT_CACHE_TIME,
  DEFAULT_RETRY_COUNT,
  DEFAULT_RETRY_DELAY,
  DEFAULT_TIMEOUT,
  MAX_RETRY_DELAY,
} from "./cache";
import type { ApiClientOptions } from "./types";

export class ApiClient {
  private memoryCache: MemoryCache;
  private localCache: LocalStorageCache;
  private deduplicator: RequestDeduplicator;

  constructor() {
    this.memoryCache = new MemoryCache();
    this.localCache = new LocalStorageCache();
    this.deduplicator = new RequestDeduplicator();
  }

  /**
   * Build i18n API URL (product data endpoints)
   */
  buildUrl(path: string): string {
    const lang = safeLocalStorage.getItem("lang") ?? "en";
    // AF-3: Call getter each time rather than using a cached value, preserving
    // configuration.backendURL's deferred-access design for test environments.
    return `${configuration.backendURL}/${lang}/api/${path}`;
  }

  /**
   * Build direct API URL (form submissions and non-i18n endpoints)
   */
  buildDirectUrl(path: string): string {
    // AF-3: Call getter each time (see buildUrl comment above).
    return `${configuration.backendURL}/${path}`;
  }

  /**
   * Generate cache key from URL
   */
  private getCacheKey(url: string): string {
    return url.replace(/[^a-zA-Z0-9]/g, "_");
  }

  /**
   * Calculate retry delay with exponential backoff and jitter
   */
  private getRetryDelay(attempt: number, baseDelay: number): number {
    const delay = baseDelay * Math.pow(2, attempt);
    const jitter = delay * 0.1 * Math.random();
    return Math.min(delay + jitter, MAX_RETRY_DELAY);
  }

  /**
   * Sleep for specified milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Check if error is retryable (network errors, 5xx, 429)
   */
  private isRetryableError(error: unknown): boolean {
    if (error instanceof Error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        return true;
      }
    }

    if (typeof error === "object" && error !== null) {
      const status = (error as { status?: number }).status;
      if (status && (status >= 500 || status === 429)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Report API errors to Sentry with request context
   */
  reportError(error: unknown, context: { method: string; url: string }): void {
    const err = error instanceof Error ? error : new Error(String(error));
    // The HTTP status, when the error carries one, as a searchable tag: a 404
    // and a 500 are different problems and must not read as one issue.
    const status = (error as { status?: unknown } | null)?.status;
    Sentry.captureException(err, {
      tags: {
        api_method: context.method,
        ...(typeof status === "number" ? { http_status: String(status) } : {}),
      },
      contexts: { request: { url: context.url, method: context.method } },
    });
  }

  /**
   * GET request with caching, deduplication, and retry
   */
  async request<T>(path: string, options: ApiClientOptions = {}): Promise<T> {
    const {
      cache = true,
      cacheTime = DEFAULT_CACHE_TIME,
      forceRefresh = false,
      retry = true,
      retryCount = DEFAULT_RETRY_COUNT,
      retryDelay = DEFAULT_RETRY_DELAY,
      timeout = DEFAULT_TIMEOUT,
      quietStatuses,
      quietTimeout = false,
    } = options;

    const url = this.buildUrl(path);
    const cacheKey = this.getCacheKey(url);
    // N-8: a status the caller handles itself is thrown but not reported.
    const report = (error: unknown) => {
      const status = (error as { status?: unknown } | null)?.status;
      if (typeof status === "number" && quietStatuses?.includes(status)) return;
      if (
        quietTimeout &&
        (error as { name?: unknown } | null)?.name === "AbortError"
      )
        return;
      this.reportError(error, { method: "GET", url });
    };

    // forceRefresh skips the cache READ and keeps the cache WRITE below, so a
    // page that must show current data refreshes the shared copy for everyone
    // instead of deleting it (V-7, IND-9).
    if (cache && !forceRefresh) {
      const memoryCached = this.memoryCache.get<T>(cacheKey);
      if (memoryCached !== null) {
        return memoryCached;
      }

      const localCached = this.localCache.get<T>(cacheKey);
      if (localCached !== null) {
        this.memoryCache.set(cacheKey, localCached, cacheTime);
        return localCached;
      }
    }

    return this.deduplicator.dedupe(cacheKey, async () => {
      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= (retry ? retryCount : 0); attempt++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);

          try {
            const response = await fetch(url, { signal: controller.signal });

            if (!response.ok) {
              // A real Error with the status attached, as the POST paths throw.
              // A plain object reached Sentry as "[object Object]" (String() of
              // it), so every failed page-data request -- 404, 500, 403 -- was
              // grouped under one title. Callers read .status and .message,
              // which this still carries.
              const error = Object.assign(
                new Error(`HTTP ${response.status}: ${response.statusText}`),
                { status: response.status },
              );

              if (
                retry &&
                this.isRetryableError({ status: response.status }) &&
                attempt < retryCount
              ) {
                await this.sleep(this.getRetryDelay(attempt, retryDelay));
                continue;
              }

              throw error;
            }

            const data = (await response.json()) as T;

            if (cache) {
              this.memoryCache.set(cacheKey, data, cacheTime);
              this.localCache.set(cacheKey, data, cacheTime);
            }

            return data;
          } finally {
            clearTimeout(timeoutId); // always clears — success, error, abort
          }
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));

          if (retry && this.isRetryableError(error) && attempt < retryCount) {
            await this.sleep(this.getRetryDelay(attempt, retryDelay));
            continue;
          }

          report(error);
          throw error;
        }
      }

      report(lastError);
      /* c8 ignore next -- lastError is always set inside the retry loop before
       * reaching this line; the ?? fallback is a safety net for static analysis. */
      throw lastError ?? new Error("Request failed");
    });
  }

  /**
   * GET shorthand
   */
  async get<T>(path: string, options?: ApiClientOptions): Promise<T> {
    return this.request<T>(path, options);
  }

  /**
   * POST to i18n endpoint
   */
  async post<T>(
    path: string,
    data: unknown,
    options: Omit<ApiClientOptions, "cache" | "cacheTime"> = {},
  ): Promise<T> {
    const url = this.buildUrl(path);
    const {
      // POST requests default to no retry — retrying non-idempotent requests
      // risks duplicate form submissions and duplicate FreeScout tickets.
      // Pass retry: true explicitly only for idempotent POST operations.
      retry = false,
      retryCount = DEFAULT_RETRY_COUNT,
      retryDelay = DEFAULT_RETRY_DELAY,
      timeout = DEFAULT_TIMEOUT,
    } = options;

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= (retry ? retryCount : 0); attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
          const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
            signal: controller.signal,
          });

          if (!response.ok) {
            if (
              retry &&
              this.isRetryableError({ status: response.status }) &&
              attempt < retryCount
            ) {
              await this.sleep(this.getRetryDelay(attempt, retryDelay));
              continue;
            }

            // The same real Error as the GET path, for the same reason.
            throw Object.assign(
              new Error(`HTTP ${response.status}: ${response.statusText}`),
              { status: response.status },
            );
          }

          return (await response.json()) as T;
        } finally {
          clearTimeout(timeoutId); // always clears — success, error, abort
        }
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (retry && this.isRetryableError(error) && attempt < retryCount) {
          await this.sleep(this.getRetryDelay(attempt, retryDelay));
          continue;
        }

        this.reportError(error, { method: "POST", url });
        throw error;
      }
    }

    this.reportError(lastError, { method: "POST", url });
    /* c8 ignore next -- same as GET: lastError is always set in the loop. */
    throw lastError ?? new Error("Request failed");
  }

  /**
   * POST to non-i18n endpoint (form submissions)
   */
  async postDirect<T>(
    path: string,
    data: unknown,
    timeout: number = DEFAULT_TIMEOUT,
  ): Promise<T> {
    const url = this.buildDirectUrl(path);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        signal: controller.signal,
      });

      const raw = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
      } catch {
        // Non-JSON body (HTML error page, plain-text rate-limit response, etc.)
      }

      if (!response.ok) {
        const message =
          (parsed.message as string) ||
          (response.status === 429
            ? "Too many requests. Please wait a few minutes and try again."
            : `HTTP ${response.status}: ${response.statusText}`);
        const err = Object.assign(new Error(message), {
          status: response.status,
          // N1: preserve field key from backend so forms can route to the right Formik field
          ...(typeof parsed.field === "string" ? { field: parsed.field } : {}),
        });
        this.reportError(err, { method: "POST", url });
        throw err;
      }

      return parsed as T;
    } finally {
      clearTimeout(timeoutId); // always clears — covers success, error, and abort
    }
  }

  /**
   * POST FormData to non-i18n endpoint (file uploads)
   */
  async postFormData<T>(
    path: string,
    formData: FormData,
    timeout: number = DEFAULT_TIMEOUT,
  ): Promise<T> {
    const url = this.buildDirectUrl(path);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData,
        signal: controller.signal,
      });

      const raw = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
      } catch {
        // Non-JSON body (HTML error page, empty 502/503, etc.)
      }

      if (!response.ok) {
        // Every support-form submission goes through this method, and the
        // shared submission throttle is 10 an hour per address — an office or
        // campus behind one NAT address reaches it. Django REST returns
        // {"detail": ...} rather than {"message": ...}, so without this branch
        // the eleventh person to ask for help saw the literal string
        // "HTTP 429: Too Many Requests" in the red banner, with nothing to
        // suggest that waiting would help. Same sentence postDirect already
        // uses, so the two paths agree.
        const message =
          (parsed.message as string) ||
          (response.status === 429
            ? "Too many requests. Please wait a few minutes and try again."
            : `HTTP ${response.status}: ${response.statusText}`);
        const err = Object.assign(new Error(message), {
          status: response.status,
          // N1: preserve field key from backend so forms can route to the right Formik field
          ...(typeof parsed.field === "string" ? { field: parsed.field } : {}),
        });
        this.reportError(err, { method: "POST (FormData)", url });
        throw err;
      }

      return parsed as T;
    } finally {
      clearTimeout(timeoutId); // always clears — covers success, error, and abort
    }
  }

  /**
   * GET from external URL (third-party APIs)
   */
  async getExternal<T>(
    url: string,
    timeout: number = DEFAULT_TIMEOUT,
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        const err = new Error(
          `HTTP ${response.status}: ${response.statusText}`,
        );
        this.reportError(err, { method: "GET (external)", url });
        throw err;
      }

      return (await response.json()) as T;
    } catch (error) {
      this.reportError(error, { method: "GET (external)", url });
      throw error;
    } finally {
      clearTimeout(timeoutId); // always clears — success, error, abort
    }
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.memoryCache.clear();
    this.localCache.clear();
  }

  /**
   * Clear cache for specific path
   */
  clearCacheForPath(path: string): void {
    const url = this.buildUrl(path);
    const cacheKey = this.getCacheKey(url);
    this.memoryCache.delete(cacheKey);
    this.localCache.delete(cacheKey);
  }
}

export const apiClient = new ApiClient();
